import * as React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

interface NavItem {
  label: string;
  href: string;
}

interface NavbarProps {
  logo?: React.ReactNode;
  companyName?: string;
  navItems?: NavItem[];
  ctaText?: string;
  onCTAClick?: () => void;
}

const defaultNavItems: NavItem[] = [
  { label: "Features", href: "#features" },
  { label: "FAQ", href: "#faq" },
  { label: "Support", href: "/support" },
];

/**
 * Navbar (Neo-Brutalist Version)
 *
 * Responsive navigation with:
 * - Solid background with black border on scroll
 * - Mobile hamburger menu with hard-shadow dropdown
 * - Smooth scroll to sections
 */
export function Navbar({
  logo,
  companyName = "Board Genie",
  navItems = defaultNavItems,
  ctaText = "Get Started",
  onCTAClick,
}: NavbarProps) {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50",
        "transition-all duration-200 ease-out",
        isScrolled
          ? "bg-[#FFFBF0] border-b-3 border-black shadow-[0_4px_0_0_#000000]"
          : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 text-[#1A1A1A] font-bold text-lg font-['Epilogue']"
          >
            {logo || (
              <div
                className={cn(
                  "w-10 h-10 rounded-xl",
                  "bg-[#FFDC00]",
                  "border-3 border-black",
                  "shadow-[3px_3px_0_0_#000000]",
                  "flex items-center justify-center",
                  "transition-all duration-150",
                  "hover:-translate-x-0.5 hover:-translate-y-0.5",
                  "hover:shadow-[4px_4px_0_0_#000000]"
                )}
              >
                <span className="text-black font-black text-sm">BG</span>
              </div>
            )}
            <span className="hidden sm:block">{companyName}</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const navLinkClass = cn(
                "px-4 py-2 rounded-xl text-sm font-semibold font-['Sora']",
                "text-[#1A1A1A] hover:text-black",
                "border-2 border-transparent",
                "hover:bg-[#FFDC00] hover:border-black",
                "hover:shadow-[3px_3px_0_0_#000000]",
                "transition-all duration-150"
              );
              return item.href.startsWith("/") ? (
                <Link key={item.href} to={item.href} className={navLinkClass}>
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={navLinkClass}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-3">
            {onCTAClick ? (
              <Button onClick={onCTAClick}>{ctaText}</Button>
            ) : (
              <Link to="/signup">
                <Button>{ctaText}</Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              "md:hidden p-2 rounded-xl",
              "text-[#1A1A1A]",
              "border-2 border-black",
              "bg-white",
              "shadow-[3px_3px_0_0_#000000]",
              "transition-all duration-150",
              "active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
            )}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={cn(
          "md:hidden overflow-hidden",
          "transition-all duration-200 ease-out",
          isMobileMenuOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="bg-[#FFFBF0] border-t-3 border-black">
          <div className="container mx-auto px-4 py-4 space-y-2">
            {navItems.map((item) => {
              const mobileLinkClass = cn(
                "block w-full text-left px-4 py-3 rounded-xl",
                "text-[#1A1A1A] font-semibold font-['Sora']",
                "border-2 border-transparent",
                "hover:bg-[#FFDC00] hover:border-black",
                "hover:shadow-[3px_3px_0_0_#000000]",
                "transition-all duration-150"
              );
              return item.href.startsWith("/") ? (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={mobileLinkClass}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item.href)}
                  className={mobileLinkClass}
                >
                  {item.label}
                </button>
              );
            })}
            <div className="pt-2">
              {onCTAClick ? (
                <Button className="w-full" onClick={() => { setIsMobileMenuOpen(false); onCTAClick(); }}>
                  {ctaText}
                </Button>
              ) : (
                <Link to="/signup" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full">{ctaText}</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
